'use client';

import type { ReactNode} from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NumberFormatValues, SourceInfo } from 'react-number-format';
import { NumericFormat } from 'react-number-format';

import classNames from 'classnames';
import { debounce } from 'lodash';
import { useIsClient } from 'usehooks-ts';
import { useAccount } from 'wagmi';

import WalletOutline from '@uikit/icons/WalletOutline';

import { numberToWei, weiToNumber } from '@shared/helpers/web3/formatters';
import { useGetTokenPriceInUSDQuery } from '@shared/hooks/query/magicSwap';

import Spinner from '@ui/uikit/components/common/Loader/Loader';
import { InfoTooltip } from '@ui/uikit/components/common/Tooltip/InfoTooltip';

import type { SwapOperationSide } from '../utilities/SwapExperienceStore';
import { truncateDecimals } from '../utilities/truncateDecimals';
import { useTokenBalance } from '../utilities/useTokenBalance';
import { SwapTokenSelector } from './SwapTokenSelector';

interface SwapUnitControlError {
  message: string;
  tooltip?: string;
}

interface SwapUnitControlProps {
  label?: string;
  data?: Partial<SwapOperationSide>;
  isDataStale?: boolean;
  isReadOnly?: boolean;
  onChange?: (updates: Partial<SwapOperationSide>) => void;
  headerControls?: ReactNode;
  controlBalanceOverrun?: boolean;
  error?: SwapUnitControlError;
}

const VALID_AMOUNT_INPUT_FORMAT = /^(0|[1-9]\d*)(\.\d{0,5})?$|^\.\d{0,5}$/;

interface SwapAmountControlProps {
  value?: number;
  isReadOnly?: boolean;
  isValueStale?: boolean;
  onChange?(value: number): void;
}

const SwapAmountControl = ({
  value,
  isReadOnly,
  isValueStale,
  onChange,
}: SwapAmountControlProps) => {
  const [internalValue, setInternalValue] = useState<string>('');

  const propagateValueChange = useMemo(() => {
    return debounce((updatedValue: number) => {
      onChange?.(updatedValue);
    }, 500);
  }, [onChange]);

  const handleValueChange = useCallback(
    (values: NumberFormatValues, sourceInfo: SourceInfo) => {
      console.log('SwapAmountControl::handleValueChange');

      if (sourceInfo.source === 'prop') {
        return;
      }

      let newAmount = values.value !== '' ? Number.parseFloat(values.value) : 0;

      if (newAmount > Number.MAX_SAFE_INTEGER) {
        newAmount = Number.MAX_SAFE_INTEGER;
      }

      setInternalValue(newAmount ? newAmount.toString() : '');

      propagateValueChange(newAmount);
    },
    [propagateValueChange],
  );

  useEffect(() => {
    setInternalValue(value ? truncateDecimals(value, 5).toString() : '');
  }, [value]);

  return (
    <NumericFormat
      className={classNames(
        'w-full border-0 bg-transparent color-neutral-600 text-right text-[24px] font-[600] text-neutral-600 focus:outline-none',
        {
          'animate-pulse-4x': isValueStale,
        },
      )}
      allowNegative={false}
      thousandSeparator
      value={internalValue}
      max={Number.MAX_SAFE_INTEGER}
      readOnly={isReadOnly}
      isAllowed={(values) => {
        return values.value === '' || VALID_AMOUNT_INPUT_FORMAT.test(values.value);
      }}
      onValueChange={!isReadOnly ? handleValueChange : undefined}
      placeholder="0.00"
    />
  );
};

const SwapUnitControl = ({
  label,
  data,
  onChange,
  isReadOnly,
  isDataStale,
  headerControls,
  error,
}: SwapUnitControlProps) => {
  const isClient = useIsClient();
  const { address } = useAccount();

  const { data: tokenBalance, isPending: isLloadingTokenBalance } = useTokenBalance({
    address,
    chainId: data?.network?.id,
    token: data?.token ?? undefined,
  });

  const formattedTokenBalance = useMemo(() => {
    return tokenBalance && data?.token
      ? truncateDecimals(weiToNumber(tokenBalance, data.token.attributes.decimal), 5).toString()
      : '0.00';
  }, [tokenBalance, data?.token]);

  const { data: tokenPriceInUsd } = useGetTokenPriceInUSDQuery({
    networkId: data?.network?.id,
    tokenAddress: data?.token?.id,
  });

  const equivalentInUsd = useMemo(() => {
    if (!data || !data.token) {
      return undefined;
    }

    if (tokenPriceInUsd) {
      return (
        weiToNumber(data.amount || 0, data.token.attributes.decimal) *
        tokenPriceInUsd.attributes.price
      );
    }

    return undefined;
  }, [data, tokenPriceInUsd]);

  const formattedEquivalentInUsd = useMemo(() => {
    return equivalentInUsd === undefined
      ? undefined
      : (equivalentInUsd === 0 ? '' : '≈') + '$' + equivalentInUsd.toFixed(2);
  }, [equivalentInUsd]);

  const handleAmountChange = useCallback(
    (value: number) => {
      onChange?.({ amount: numberToWei(value, data?.token?.attributes.decimal).toBigInt() });
    },
    [data?.token?.attributes.decimal, onChange],
  );

  return (
    <div
      className={classNames(
        'rounded-[16px] p-[16px] bg-primary-50 border-2 border-primary-50 transition-colors transition-1000',
        { 'border-red-600': !!error },
      )}
    >
      <div className="flex flex-col gap-[12px] text-neutral-400">
        <div className="grid grid-cols-2 gap-[8px] text-[14px]">
          <div className="self-center">{label}</div>

          <div className="flex flex-row justify-end font-bold">
            <div className="inline-flex flex-row gap-[16px] items-center content-end">
              {isClient && address && (
                <div className="flex flex-row gap-[4px] items-center">
                  <WalletOutline className="icon-[24px]" />
                  <span>
                    {isLloadingTokenBalance ? (
                      <Spinner size="sm" className="m-auto" />
                    ) : (
                      formattedTokenBalance
                    )}
                  </span>
                </div>
              )}
              {headerControls}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-[16px] [&>*]:grow [&>*]:justify-between sm:[&>*]:justify-none sm:[&>*]:w-full">
          <SwapTokenSelector
            label={label}
            network={data?.network}
            token={data?.token ?? undefined}
            onChange={onChange}
          />

          <div className="min-w-0">
            <SwapAmountControl
              value={
                data?.amount
                  ? weiToNumber(data?.amount, data?.token?.attributes.decimal)
                  : undefined
              }
              isReadOnly={isReadOnly}
              isValueStale={isDataStale}
              onChange={handleAmountChange}
            />
            <div
              className={classNames(
                'flex flex-row items-center gap-[4px] text-red-600 transition-opacity transition-1000 min-w-0 w-full',
                error ? 'opacity-100' : 'opacity-0',
              )}
            >
              <div
                className={classNames('truncate text-right text-[12px] min-w-0 grow')}
                title={error?.message}
              >
                {/* Adding an empty string below is important to avoid resizing when there are no errors. */}
                {error?.message || '—'}
              </div>
              {error?.tooltip && <InfoTooltip message={error.tooltip} />}
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div>{data?.token?.attributes.name}</div>
          <span
            className={classNames({
              'animate-pulse-4x': isDataStale,
            })}
          >
            {formattedEquivalentInUsd !== undefined ? (
              formattedEquivalentInUsd
            ) : (
              <Spinner size="sm" className="m-auto" />
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export { SwapUnitControl, type SwapUnitControlError };
